import React from 'react';
import type { Presupuesto, EmpresaConfig } from '@/lib/types';
import { calcularTotales, formatEUR, formatFecha } from '@/lib/calc';

interface Props {
  presupuesto: Presupuesto;
  config: EmpresaConfig;
}

export function PresupuestoPDF({ presupuesto: p, config }: Props) {
  const t = calcularTotales(p.lineas);
  const primary = config.colorPrimario || '#1e3a5f';

  return (
    <div
      id="pdf-content"
      style={{
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        fontSize: '11px',
        color: '#1a1a2e',
        background: '#ffffff',
        width: '210mm',
        minHeight: '297mm',
        margin: '0 auto',
        padding: '14mm 16mm 14mm 16mm',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {/* ── Cabecera ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8mm' }}>
        {/* Logo + empresa */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {config.logoURL && (
            <img
              src={config.logoURL}
              alt="Logo"
              style={{ height: '52px', width: '52px', objectFit: 'contain', borderRadius: '6px' }}
            />
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: '16px', color: primary, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              {config.nombre || 'Mi Empresa'}
            </div>
            {config.nif && <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>{config.nif}</div>}
            {config.direccion && <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '1px' }}>{config.direccion}</div>}
            {(config.cp || config.ciudad) && (
              <div style={{ fontSize: '10px', color: '#4b5563' }}>{[config.cp, config.ciudad].filter(Boolean).join(' ')}</div>
            )}
            {config.telefono && <div style={{ fontSize: '10px', color: '#4b5563' }}>{config.telefono}</div>}
            {config.email && <div style={{ fontSize: '10px', color: '#4b5563' }}>{config.email}</div>}
          </div>
        </div>

        {/* Número y fechas */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 800, fontSize: '20px', color: primary, letterSpacing: '-0.5px' }}>
            PRESUPUESTO
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', fontWeight: 600, color: '#374151', marginTop: '4px' }}>
            {p.numero}
          </div>
          <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '6px' }}>
            <span style={{ fontWeight: 500 }}>Fecha: </span>{formatFecha(p.fecha)}
          </div>
          <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
            <span style={{ fontWeight: 500 }}>Válido hasta: </span>{formatFecha(p.fechaValidez)}
          </div>
          <div style={{
            marginTop: '8px', display: 'inline-block', padding: '2px 10px', borderRadius: '20px',
            background: p.estado === 'Aceptado' ? '#d1fae5' : p.estado === 'Enviado' ? '#dbeafe' : p.estado === 'Rechazado' ? '#fee2e2' : '#f3f4f6',
            color: p.estado === 'Aceptado' ? '#065f46' : p.estado === 'Enviado' ? '#1d4ed8' : p.estado === 'Rechazado' ? '#991b1b' : '#374151',
            fontWeight: 600, fontSize: '9px', letterSpacing: '0.5px', textTransform: 'uppercase' as const,
          }}>
            {p.estado}
          </div>
        </div>
      </div>

      {/* Divisor */}
      <div style={{ height: '2px', background: primary, marginBottom: '6mm', borderRadius: '1px' }} />

      {/* ── Datos cliente / aseguradora ──────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: p.aseguradora || p.numeroSiniestro ? '1fr 1fr' : '1fr', gap: '8mm', marginBottom: '7mm' }}>
        {/* Cliente */}
        <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '10px 12px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontWeight: 700, fontSize: '9px', textTransform: 'uppercase' as const, letterSpacing: '1px', color: primary, marginBottom: '6px' }}>
            Destinatario
          </div>
          <div style={{ fontWeight: 600, fontSize: '12px', color: '#111827' }}>{p.clienteNombre || '—'}</div>
          {p.clienteNIF && <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '2px' }}>NIF/CIF: {p.clienteNIF}</div>}
          {p.clienteDireccion && <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '2px' }}>{p.clienteDireccion}</div>}
          {p.clienteEmail && <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '2px' }}>{p.clienteEmail}</div>}
          {p.clienteTelefono && <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '2px' }}>{p.clienteTelefono}</div>}
        </div>

        {/* Datos siniestro */}
        {(p.aseguradora || p.numeroSiniestro || p.matricula) && (
          <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '10px 12px', border: '1px solid #bfdbfe' }}>
            <div style={{ fontWeight: 700, fontSize: '9px', textTransform: 'uppercase' as const, letterSpacing: '1px', color: primary, marginBottom: '6px' }}>
              Datos del Siniestro
            </div>
            {p.aseguradora && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' as const }}>Aseguradora: </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#111827' }}>{p.aseguradora}</span>
              </div>
            )}
            {p.numeroSiniestro && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' as const }}>Nº Siniestro: </span>
                <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: '#1d4ed8' }}>{p.numeroSiniestro}</span>
              </div>
            )}
            {p.matricula && (
              <div>
                <span style={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' as const }}>Matrícula: </span>
                <span style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: '#111827' }}>{p.matricula}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Tabla de líneas ──────────────────────────────────────── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '6mm', fontSize: '10px' }}>
        <thead>
          <tr style={{ background: primary, color: '#ffffff' }}>
            <th style={{ textAlign: 'left', padding: '7px 10px', fontWeight: 600, letterSpacing: '0.3px', borderRadius: '0' }}>Descripción</th>
            <th style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 600, width: '50px' }}>Uds.</th>
            <th style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 600, width: '80px' }}>P. Unit.</th>
            <th style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 600, width: '55px' }}>Dto.%</th>
            <th style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 600, width: '50px' }}>IVA%</th>
            <th style={{ textAlign: 'right', padding: '7px 10px', fontWeight: 600, width: '90px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {p.lineas.length === 0 && (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '11px' }}>
                Sin partidas
              </td>
            </tr>
          )}
          {p.lineas.map((l, i) => {
            const bruto = l.unidades * l.precioUnitario;
            const neto = bruto * (1 - l.descuento / 100);
            const total = neto * (1 + l.iva / 100);
            return (
              <tr key={l.id} style={{ background: i % 2 === 0 ? '#ffffff' : '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '7px 10px', color: '#111827', lineHeight: 1.4 }}>{l.descripcion || '—'}</td>
                <td style={{ textAlign: 'right', padding: '7px 8px', fontFamily: "'IBM Plex Mono', monospace", color: '#374151' }}>
                  {l.unidades % 1 === 0 ? l.unidades : l.unidades.toFixed(2)}
                </td>
                <td style={{ textAlign: 'right', padding: '7px 8px', fontFamily: "'IBM Plex Mono', monospace", color: '#374151' }}>
                  {formatEUR(l.precioUnitario)}
                </td>
                <td style={{ textAlign: 'right', padding: '7px 8px', fontFamily: "'IBM Plex Mono', monospace", color: l.descuento > 0 ? '#059669' : '#9ca3af' }}>
                  {l.descuento > 0 ? `-${l.descuento}%` : '—'}
                </td>
                <td style={{ textAlign: 'right', padding: '7px 8px', fontFamily: "'IBM Plex Mono', monospace", color: '#374151' }}>
                  {l.iva}%
                </td>
                <td style={{ textAlign: 'right', padding: '7px 10px', fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: '#111827' }}>
                  {formatEUR(total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Totales ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6mm' }}>
        <div style={{ width: '240px' }}>
          {[
            { label: 'Subtotal', value: formatEUR(t.subtotal), show: true },
            { label: `Descuentos`, value: `− ${formatEUR(t.totalDescuento)}`, show: t.totalDescuento > 0 },
            { label: 'Base imponible', value: formatEUR(t.baseImponible), show: true, bold: true },
            ...t.gruposIVA.map(g => ({
              label: `IVA ${g.tipo}%`, value: formatEUR(g.cuota), show: g.cuota > 0,
            })),
          ].filter(r => r.show).map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: (r as { bold?: boolean }).bold ? 600 : 400 }}>{r.label}</span>
              <span style={{ fontSize: '10px', fontFamily: "'IBM Plex Mono', monospace", fontWeight: (r as { bold?: boolean }).bold ? 600 : 400, color: '#111827' }}>{r.value}</span>
            </div>
          ))}

          {/* Total final */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 12px', marginTop: '6px',
            background: primary, borderRadius: '8px', color: '#ffffff',
          }}>
            <span style={{ fontWeight: 700, fontSize: '12px', letterSpacing: '0.5px' }}>TOTAL</span>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 800, fontSize: '15px', letterSpacing: '-0.3px' }}>
              {formatEUR(t.totalGeneral)}
            </span>
          </div>
        </div>
      </div>

      {/* ── Notas y condiciones ───────────────────────────────────── */}
      {(p.notas || p.condiciones) && (
        <div style={{ display: 'grid', gridTemplateColumns: p.notas && p.condiciones ? '1fr 1fr' : '1fr', gap: '6mm', marginBottom: '6mm' }}>
          {p.notas && (
            <div>
              <div style={{ fontWeight: 700, fontSize: '9px', textTransform: 'uppercase' as const, letterSpacing: '1px', color: primary, marginBottom: '4px' }}>Observaciones</div>
              <div style={{ fontSize: '10px', color: '#4b5563', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{p.notas}</div>
            </div>
          )}
          {p.condiciones && (
            <div>
              <div style={{ fontWeight: 700, fontSize: '9px', textTransform: 'uppercase' as const, letterSpacing: '1px', color: primary, marginBottom: '4px' }}>Condiciones</div>
              <div style={{ fontSize: '10px', color: '#4b5563', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{p.condiciones}</div>
            </div>
          )}
        </div>
      )}

      {/* IBAN */}
      {config.iban && (
        <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '8px 12px', border: '1px solid #e5e7eb', marginBottom: '6mm' }}>
          <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: '#6b7280' }}>Datos bancarios — IBAN: </span>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', fontWeight: 600, color: '#111827', letterSpacing: '0.5px' }}>{config.iban}</span>
        </div>
      )}

      {/* ── Pie de página ─────────────────────────────────────────── */}
      <div style={{
        position: 'absolute', bottom: '10mm', left: '16mm', right: '16mm',
        borderTop: '1px solid #e5e7eb', paddingTop: '6px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: '9px', color: '#9ca3af' }}>{config.piePagina}</span>
        <span style={{ fontSize: '9px', color: '#9ca3af' }}>{config.web}</span>
      </div>
    </div>
  );
}
